import { type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { isSingletonBlockType } from '@provision/landing-page-utils';
import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  BLOCK_TYPE_LABELS,
  type LandingPageBlockType,
  type LandingPageFormValues,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { createLandingPageBlock } from 'src/pages/Settings/memberships/utils/create-landing-page-block';
import { validateLandingPageBlocks } from 'src/pages/Settings/memberships/utils/validate-landing-page-blocks';
import { useConfirmationModal } from 'src/utils/hooks/use-confirmation-modal';

const DRAG_ACTIVATION_DISTANCE_PX = 4;

export const useLandingPageBlocks = (readOnly: boolean) => {
  const { confirm } = useConfirmationModal();
  const { control } = useFormContext<LandingPageFormValues>();
  const { fields, move, append, remove } = useFieldArray<
    LandingPageFormValues,
    'blocks',
    '_fieldId'
  >({
    control,
    name: 'blocks',
    keyName: '_fieldId',
    rules: { validate: validateLandingPageBlocks },
  });
  const [expandedBlockIds, setExpandedBlockIds] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
  );

  const blockIds = useMemo(() => fields.map((field) => field.id), [fields]);
  const presentTypes = useMemo(() => new Set(fields.map((field) => field.type)), [fields]);

  const handleExpandedChange = useCallback((blockId: string, expanded: boolean) => {
    setExpandedBlockIds((current) =>
      expanded ? [...current, blockId] : current.filter((id) => id !== blockId),
    );
  }, []);

  // Uniform row heights for the length of the drag; dnd-kit measures animating bodies badly.
  const handleDragStart = useCallback(() => {
    setExpandedBlockIds([]);
  }, []);

  const handleMove = useCallback(
    (from: number, to: number) => {
      if (!readOnly) {
        move(from, to);
      }
    },
    [move, readOnly],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      const from = fields.findIndex((field) => field.id === String(active.id));
      const to = fields.findIndex((field) => field.id === String(over.id));

      if (from !== -1 && to !== -1) {
        handleMove(from, to);
      }
    },
    [fields, handleMove],
  );

  const handleAdd = useCallback(
    (type: LandingPageBlockType) => {
      // Guarded here too: the menu can be stale mid-render.
      if (
        readOnly ||
        (isSingletonBlockType(type) && fields.some((field) => field.type === type))
      ) {
        return;
      }

      const block = createLandingPageBlock(type, fields);
      append(block);
      setExpandedBlockIds((current) => [...current, block.id]);
    },
    [append, fields, readOnly],
  );

  const handleRemove = useCallback(
    async (index: number, blockId: string) => {
      if (readOnly) {
        return;
      }

      const type = fields[index]?.type;
      const confirmed = await confirm({
        title: 'Remove block',
        body: `Remove the ${type ? BLOCK_TYPE_LABELS[type] : 'block'} from this page?`,
        variant: 'delete',
      });

      if (!confirmed) {
        return;
      }

      remove(index);
      setExpandedBlockIds((current) => current.filter((id) => id !== blockId));
    },
    [confirm, fields, readOnly, remove],
  );

  return {
    fields,
    blockIds,
    presentTypes,
    expandedBlockIds,
    sensors,
    handleExpandedChange,
    handleDragStart,
    handleDragEnd,
    handleMove,
    handleAdd,
    handleRemove,
  };
};
